/* Owns the `data-theme` attribute: reads the preference from
   localStorage['sac.theme'], resolves `system` against prefers-color-scheme and
   stamps a concrete value on <html>. Dark is the default and the only theme
   SPEC §9 ships; light is a token swap declared in index.css. */

import { useCallback, useEffect, useState } from 'react'

export type ThemePreference = 'dark' | 'light' | 'system'
export type ResolvedTheme = 'dark' | 'light'

const STORAGE_KEY = 'sac.theme'

function safeRead(): ThemePreference {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored === 'dark' || stored === 'light' || stored === 'system') return stored
  } catch {
    // Private mode and storage-blocked browsers must still render.
  }
  return 'dark'
}

function safeWrite(value: ThemePreference): void {
  try {
    localStorage.setItem(STORAGE_KEY, value)
  } catch {
    /* Losing the preference is acceptable; failing to switch theme is not. */
  }
}

function prefersLight(): boolean {
  // jsdom has no matchMedia, so every caller must tolerate its absence.
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false
  return window.matchMedia('(prefers-color-scheme: light)').matches
}

export function resolveTheme(preference: ThemePreference): ResolvedTheme {
  if (preference === 'system') return prefersLight() ? 'light' : 'dark'
  return preference
}

/** Stamps a concrete theme, so the CSS never has to guess and there is no flash. */
export function applyTheme(preference: ThemePreference): ResolvedTheme {
  const resolved = resolveTheme(preference)
  if (typeof document !== 'undefined') {
    document.documentElement.setAttribute('data-theme', resolved)
  }
  return resolved
}

export function getThemePreference(): ThemePreference {
  return safeRead()
}

export function setThemePreference(preference: ThemePreference): ResolvedTheme {
  safeWrite(preference)
  return applyTheme(preference)
}

/** Applies the stored preference before first paint of the app tree. */
export function initTheme(): ResolvedTheme {
  return applyTheme(safeRead())
}

export function useTheme(): {
  preference: ThemePreference
  resolved: ResolvedTheme
  setPreference: (next: ThemePreference) => void
} {
  const [preference, setPreferenceState] = useState<ThemePreference>(() => safeRead())
  const [resolved, setResolved] = useState<ResolvedTheme>(() => resolveTheme(safeRead()))

  useEffect(() => {
    if (preference !== 'system') return
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return
    const media = window.matchMedia('(prefers-color-scheme: light)')
    const listener = (): void => setResolved(applyTheme('system'))
    media.addEventListener('change', listener)
    return () => media.removeEventListener('change', listener)
  }, [preference])

  const setPreference = useCallback((next: ThemePreference) => {
    setPreferenceState(next)
    setResolved(setThemePreference(next))
  }, [])

  return { preference, resolved, setPreference }
}
